import { useMemo, useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { GenericTable, NumberedView } from 'components/tables/generic-table'
import { LessonActionDropdown } from 'components/action-dropdown'
import { CopyToClassOverlay } from 'lesson-builder/copy-to-class-overlay'
import { ConfirmationOverlay } from 'components/confirmation-overlay'
import styles from './table.module.css'
// Reused as-is, not redefined here. AllForOne_LessonLibrary.html specifies
// its own .subj-tag/.tag-sci etc colors, which already disagree in places
// with this app's one established subject-tag palette (e.g. its .tag-social
// is peach; this app's card-tag-social is the lavender THIS mockup calls
// .tag-other). Rather than add a 3rd, slightly-different variant just for
// this table, reusing the same global classes dashboard/classroom-card/
// classroom-detail already render keeps the whole app's subject-color
// language consistent -- a mockup-wide color refresh, if wanted, is its
// own separate task, not something to fold into adding 2 missing columns.
import 'css/components/classrooms/classroom-card.css'
import moreIcon from 'img/more-vert.svg'
import moreIconWhite from 'img/more-vert-white.svg'
import viewGradesIcon from 'img/view-grades.svg'
import { useRenameLessonMutation, useDeleteLessonMutation } from 'util/rtk-api'
import { LessonSearchDetails } from '@all-for-one/shared-types'
import { classifySubject, SUBJECT_TAG_CLASS } from 'util/subject-tags'
import { deriveGradeBand } from 'util/grade-band'
// Accept lessons as a prop per requirement

const formatDate = (ts?: number) => {
	if (!ts) return '-'
	const d = new Date(ts)
	const [ , month, day, year ] = d.toDateString().split(' ')
	return `${month} ${day}, ${year}`
}

interface ActionCellProps {
	lessonId: string
	lessonName: string
	isSelected: boolean
	onStartRename: (lessonId: string, currentName: string) => void
	onStartDuplicate: (lessonId: string, lessonName: string, assignedClassId?: string, grades?: string[]) => void
	onDeleteLesson: (lessonId: string, lessonName: string) => void
	isDeletingId: string | null
	hasGradedResults?: boolean
	assignedClassId?: string
	grades?: string[]
}

/* The library's status is a badge, not a word in a cell:
   AllForOne_LessonLibrary.html's `.status-badge` plus one of
   `.status-draft` / `.status-ready` / `.status-graded`, each with its own
   small icon. Her icons are inlined so they take the badge's colour through
   currentColor, which is what makes the same pencil read terracotta on a chip
   and ink-brown here.

   'Graded' is her third state and it REPLACES 'Ready' -- every row in her
   markup carries exactly one badge, and a lesson that has been marked is more
   usefully described as graded than as ready to run. We already carry
   `hasGradedResults` for the row's action menu, so nothing new is fetched.

   Anything we don't recognise falls through to the draft treatment rather
   than rendering an unstyled word, the same way the subject pill does. */
const STATUS_ICON: Record<string, React.ReactNode> = {
	draft: (
		<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
			<path d="M12 20h9" />
			<path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
		</svg>
	),
	ready: (
		<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
			<polyline points="20 6 9 17 4 12" />
		</svg>
	),
	graded: (
		<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
			<circle cx="12" cy="12" r="10" />
			<polyline points="16.5 9 10.5 15 7.5 12" />
		</svg>
	),
}

const LessonStatusBadge = ({ status, hasGradedResults }: { status?: string, hasGradedResults?: boolean }) => {
	const named = (status || '').trim().toLowerCase()
	const key = hasGradedResults ? 'graded' : (named === 'ready' ? 'ready' : 'draft')
	const label = key === 'graded' ? 'Graded' : (status || 'Draft')
	return (
		<span className={`${styles.statusBadge} ${styles[key]}`}>
			{STATUS_ICON[key]}
			{label}
		</span>
	)
}

const ActionCell = ({ lessonId, lessonName, isSelected, onStartRename, onStartDuplicate, onDeleteLesson, isDeletingId, hasGradedResults, assignedClassId, grades }: ActionCellProps) => {
	const [open, setOpen] = useState(false)
	const [opensUp, setOpensUp] = useState(false)
	const dropdownRef = useRef<HTMLDivElement | null>(null)
	const buttonRef = useRef<HTMLImageElement | null>(null)
	const toggle = () => {
		if (!open && buttonRef.current) {
			const rect = buttonRef.current.getBoundingClientRect()
			setOpensUp(window.innerHeight - rect.bottom < 120)
		}
		setOpen(v => !v)
	}

	useEffect(() => {
		const handleClick = (e: MouseEvent) => {
			if (open && dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
				setOpen(false)
			}
		}
		document.addEventListener('mousedown', handleClick)
		return () => document.removeEventListener('mousedown', handleClick)
	}, [open])

	const onDuplicateLesson = () => {
		setOpen(false)
		onStartDuplicate(lessonId, lessonName, assignedClassId, grades)
	}

	const onRenameLesson = () => {
		setOpen(false)
		onStartRename(lessonId, lessonName)
	}

	const handleDelete = () => {
		setOpen(false)
		onDeleteLesson(lessonId, lessonName)
	}

	return (
		<td style={open ? { zIndex: 2 } : undefined}>
			<div className={styles.actionCell} ref={dropdownRef}>
				<img ref={buttonRef} src={isSelected ? moreIconWhite : moreIcon} alt="More" onClick={toggle} />
				<div>
					{open && (
						<LessonActionDropdown
							onDuplicateLesson={onDuplicateLesson}
							onRenameLesson={onRenameLesson}
							onDeleteLesson={handleDelete}
							isDeleting={isDeletingId === lessonId}
							hasGradedResults={hasGradedResults}
							opensUp={opensUp}
						/>
					)}
				</div>
			</div>
		</td>
	)
}

export const LessonLibraryTable = ({ lessons }: { lessons: LessonSearchDetails[] }) => {
	const navigate = useNavigate()
	const [renameLesson] = useRenameLessonMutation()
	const [deleteLesson] = useDeleteLessonMutation()
	const [renamingLessonId, setRenamingLessonId] = useState<string | null>(null)
	const [renameValue, setRenameValue] = useState('')
	const renameInputRef = useRef<HTMLInputElement | null>(null)
	const [deletingLessonId, setDeletingLessonId] = useState<string | null>(null)
	const [deleteConfirm, setDeleteConfirm] = useState<{ lessonId: string; lessonName: string } | null>(null)

	// Duplicate overlay state
	const [duplicateOverlay, setDuplicateOverlay] = useState<{
		lessonId: string
		lessonName: string
		currentClassId: string
		currentGrades: string[]
	} | null>(null)

	const handleStartRename = (lessonId: string, currentName: string) => {
		setRenamingLessonId(lessonId)
		setRenameValue(currentName)
	}

	const handleStartDuplicate = (lessonId: string, lessonName: string, assignedClassId?: string, grades?: string[]) => {
		setDuplicateOverlay({
			lessonId,
			lessonName,
			currentClassId: assignedClassId || '',
			currentGrades: grades || []
		})
	}

	const handleDeleteLesson = (lessonId: string, lessonName: string) => {
		setDeleteConfirm({ lessonId, lessonName })
	}

	const handleConfirmDelete = async () => {
		if (!deleteConfirm) return
		const { lessonId } = deleteConfirm
		setDeletingLessonId(lessonId)
		try {
			await deleteLesson(lessonId).unwrap()
			setDeleteConfirm(null)
		} catch (error) {
			console.error('Error deleting lesson', error)
		} finally {
			setDeletingLessonId(null)
		}
	}

	const handleCancelRename = () => {
		setRenamingLessonId(null)
		setRenameValue('')
	}

	const handleConfirmRename = async () => {
		if (renamingLessonId && renameValue.trim()) {
			try {
				await renameLesson({ lessonId: renamingLessonId, name: renameValue.trim() })
			} catch (error) {
				console.error('Error renaming lesson', error)
			}
		}
		handleCancelRename()
	}

	const handleRenameKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			handleConfirmRename()
		} else if (e.key === 'Escape') {
			handleCancelRename()
		}
	}

	useEffect(() => {
		if (renamingLessonId && renameInputRef.current) {
			renameInputRef.current.focus()
			renameInputRef.current.select()
		}
	}, [renamingLessonId])

    const rows = useMemo(() => lessons.map(l => ({
        ...l,
        gradeBand: deriveGradeBand(l.grades)
    })), [lessons])

	const columns = [
		{
			key: 'name',
			header: 'Name',
			render: (name: string, index: number, row: any) => {
				if (renamingLessonId === row.lessonId) {
					return (
						<div className={styles.renameCell}>
							<input
								ref={renameInputRef}
								type="text"
								value={renameValue}
								onChange={(e) => setRenameValue(e.target.value)}
								onKeyDown={handleRenameKeyDown}
								onBlur={handleConfirmRename}
								className={styles.renameInput}
							/>
						</div>
					)
				}
				return (
					<div className={styles.nameCell}>
						<Link className={styles.lessonLink} to={`/lesson-builder?lessonId=${row.lessonId}`}>{name}</Link>
						{row.hasGradedResults && row.assignedClassId && (
							<Link
								className={styles.gradesLink}
								to={`/classrooms/${row.assignedClassId}/lessons/${row.lessonId}`}
							>
								<img src={viewGradesIcon} alt="" className={styles.gradesIcon} />
								Grades
							</Link>
						)}
					</div>
				)
			}
		},
		{ key: 'gradeBand', header: 'Grade band', className: styles.gradeBandCol },
		{
			key: 'subject',
			header: 'Subject',
			className: styles.subjectCol,
			render: (subject: string) => (
				<span className={`card-subj-tag card-subj-tag-table ${SUBJECT_TAG_CLASS[classifySubject(subject)]}`}>
					<span className="card-subj-tag-dot" />
					{/* Her label, not ours. AllForOne_ClassroomDetail_v2.html:916 is
					    `esc(klass.subject || 'No subject')` -- the same fallback,
					    her wording. "Unassigned subject" was invented here and made
					    the pill 156px wide, which is 26px past her 130px Subject
					    track and the single biggest reason this table measured 87px
					    wider than its container.

					    Note this is a LABEL WE AUTHOR, so the fix is to write one
					    that fits, not to ellipsize it. Her ellipsis is on
					    `.lesson-link` only -- the lesson NAME, whose length a
					    teacher controls. */}
					{subject || 'No subject'}
				</span>
			)
		},
		{ key: 'lastViewed', header: 'Last viewed', className: styles.dateCol, render: (v: number) => formatDate(v) },
		{ key: 'lastModified', header: 'Last modified', className: styles.dateCol, render: (v: number) => formatDate(v) },
		{
			key: 'status',
			header: 'Status',
			className: styles.statusCol,
			render: (status: string, _index: number, row: { hasGradedResults?: boolean }) => (
				<LessonStatusBadge status={status} hasGradedResults={row?.hasGradedResults} />
			)
		}
	]

	return (
		<>
			<div className={styles.tableContainer}>
				<GenericTable
					columns={columns}
					data={rows}
					rowKey="lessonId"
					style={{}}
					renderHeader={true}
					renderMoreActions={true}
					createMoreActionCell={(lessonId: string, isSelected: boolean) => {
						const lesson = rows.find(r => r.lessonId === lessonId)
						return (
							<ActionCell
								lessonId={lessonId}
								lessonName={lesson?.name || ''}
								isSelected={isSelected}
								onStartRename={handleStartRename}
								onStartDuplicate={handleStartDuplicate}
								onDeleteLesson={handleDeleteLesson}
								isDeletingId={deletingLessonId}
								hasGradedResults={lesson?.hasGradedResults}
								assignedClassId={lesson?.assignedClassId}
								grades={lesson?.grades}
							/>
						)
					}}
					doubleClickRowAction={(lessonId: string) => navigate(`/lesson-builder?lessonId=${lessonId}`)}
				/>
			</div>
			{duplicateOverlay && (
				<CopyToClassOverlay
					lessonId={duplicateOverlay.lessonId}
					lessonName={duplicateOverlay.lessonName}
					currentClassId={duplicateOverlay.currentClassId}
					currentGrades={duplicateOverlay.currentGrades}
					onDismiss={() => setDuplicateOverlay(null)}
				/>
			)}
			{deleteConfirm && (
				<ConfirmationOverlay
					title="Delete Lesson"
					message={`Are you sure you want to delete "${deleteConfirm.lessonName}"? This cannot be undone.`}
					confirmLabel="Delete"
					onConfirm={handleConfirmDelete}
					onDismiss={() => setDeleteConfirm(null)}
					isLoading={deletingLessonId === deleteConfirm.lessonId}
				/>
			)}
		</>
	)
}


